import React, { useState, useEffect, useMemo } from "react";
import {
  getAgenticWorkflowTasks,
  getPodInstances,
} from "@/services/api/agentic";
import type { WorkflowTask, PodInstance } from "@/services/api/agentic/types";
import {
  KanbanBoard,
  type KanbanColumnData,
  type KanbanCardData,
} from "@/components/kanban";
import { workflowTaskStatusToColumnId } from "@/utils/workflowTaskKanban";

export function InstanceTasks() {
  const [tasks, setTasks] = useState<WorkflowTask[]>([]);
  const [podInstances, setPodInstances] = useState<PodInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const instanceMap = useMemo(() => {
    const map = new Map<string, PodInstance>();
    podInstances.forEach((instance) => map.set(instance.id, instance));
    return map;
  }, [podInstances]);

  const initialKanbanColumns = useMemo(
    (): KanbanColumnData[] => [
      {
        id: "todo",
        title: "To Do",
        color: "#A0AEC0",
        cards: [],
      },
      {
        id: "blocked",
        title: "Blocked",
        color: "#EF4444",
        cards: [],
      },
      {
        id: "inprogress",
        title: "In Progress",
        color: "#63B3ED",
        cards: [],
      },
      {
        id: "done",
        title: "Done",
        color: "#68D391",
        cards: [],
      },
    ],
    []
  );

  const [kanbanColumns, setKanbanColumns] =
    useState<KanbanColumnData[]>(initialKanbanColumns);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [fetchedTasks, fetchedInstancesData] = await Promise.all([
          getAgenticWorkflowTasks(),
          getPodInstances({ limit: 500 }),
        ]);

        setTasks(Array.isArray(fetchedTasks) ? fetchedTasks : []);

        if (fetchedInstancesData && (fetchedInstancesData as any).results) {
          setPodInstances((fetchedInstancesData as any).results);
        } else if (Array.isArray(fetchedInstancesData)) {
          setPodInstances(fetchedInstancesData);
        } else {
          console.warn(
            "Unexpected format for fetched pod instances:",
            fetchedInstancesData
          );
          setPodInstances([]);
        }
      } catch (err) {
        console.error("Error fetching data for aggregated tasks view:", err);
        setError(
          err instanceof Error
            ? `Failed to load data: ${err.message}`
            : "An unknown error occurred while fetching data"
        );
        setTasks([]);
        setPodInstances([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    if (loading || !tasks || podInstances.length === 0) {
      if (!loading && tasks.length > 0 && podInstances.length === 0) {
        // Tasks exist but no pod instances loaded yet - continue to process tasks
      } else if (loading) {
        return;
      }
    }

    const taskCards: KanbanCardData[] = tasks.map((task) => {
      const instance = instanceMap.get(task.pod_instance_id);
      return {
        id: task._id,
        title: task.name,
        content: task.description,
        columnId: workflowTaskStatusToColumnId(task.status?.current_status),
        assigneeName: task.assignee?.name,
        status: task?.status,
        instanceId: task.pod_instance_id,
        instanceName: task.pod_instance?.name || "[Instance]",
      };
    });

    const newKanbanColumns = initialKanbanColumns.map((column) => ({
      ...column,
      cards: taskCards.filter((card) => card.columnId === column.id),
    }));
    setKanbanColumns(newKanbanColumns);
  }, [tasks, podInstances, loading, initialKanbanColumns, instanceMap]);

  const handleCardMove = (
    cardId: string,
    sourceColumnId: string,
    destinationColumnId: string,
    newIndex: number
  ) => {
    console.log(
      `Card ${cardId} moved from ${sourceColumnId} to ${destinationColumnId} at index ${newIndex}. Backend update needed.`
    );
  };

  if (loading) {
    return <div className="p-4 text-center">Loading all tasks...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500 text-center">Error: {error}</div>;
  }

  return (
    <div className="p-4">
      {tasks.length === 0 && !loading && (
        <p className="text-gray-500 dark:text-gray-400 text-center">
          No tasks found.
        </p>
      )}
      {tasks.length > 0 && (
        <KanbanBoard
          initialColumns={kanbanColumns}
          onCardMove={handleCardMove}
          variant="compact"
        />
      )}
    </div>
  );
}
